/**
 * Unified Promotions Manager - Multi-Currency Edition
 * 
 * Manages both GWP (Gift With Purchase) and Tiered Discounts simultaneously across multiple currencies
 * 
 * Configuration: Hardcoded in CONFIG below
 * 
 * GWP Feature:
 * - Adds/removes gift item when cart subtotal crosses threshold (currency-specific)
 * - Gift item variant ID, threshold, and discount code can differ per currency
 * 
 * Tiered Discounts Feature:
 * - Multiple tiers with different thresholds and discount codes per currency
 * - Example: USD might have 10%/15%/20% at $35/$55/$75
 * - CAD might have 10%/15%/20% at $50/$75/$105
 * 
 * Discount Code Management:
 * - Attempts to apply discount codes as configured
 * - Falls back gracefully if multiple codes aren't supported
 * 
 * Only active in currencies that are configured
 * Debug mode: Controlled via DEBUG flag below
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIGURATION - Edit values below for your store
  // ============================================================================
  
  const CONFIG = {
    DEBUG: false, // Set to true for detailed console logging
    
    // GWP Configuration by Currency
    // Format: { currency: { variantId, thresholdCents, discountCode } }
    GWP: {
      USD: {
        variantId: 50513520361756,
        thresholdCents: 4000, // $40 USD
        discountCode: 'FREEGIFT'
      },
      CAD: {
        variantId: 46576432316712,
        thresholdCents: 8500, // $85 CAD
        discountCode: 'FREEGIFT'
      }
      // Add more currencies as needed:
      // EUR: { variantId: 123456, thresholdCents: 4500, discountCode: 'FREEGIFT' }
    },
    
    // Tiered Discounts by Currency
    // Format: { currency: [ {code, thresholdCents, percentage, label}, ... ] }
    // Must be ordered from highest threshold to lowest for proper evaluation
    TIERS: {
      USD: [
        {
          code: 'BFCM_20',
          thresholdCents: 7500, // $75 USD
          percentage: 20,
          label: '20% off $75+'
        },
        {
          code: 'BFCM_15',
          thresholdCents: 5500, // $55 USD
          percentage: 15,
          label: '15% off $55+'
        },
        {
          code: 'BFCM_10',
          thresholdCents: 3500, // $35 USD
          percentage: 10,
          label: '10% off $35+'
        }
      ],
      CAD: [
        {
          code: 'BFCM_20',
          thresholdCents: 10500, // $105 CAD
          percentage: 20,
          label: '20% off $105+'
        },
        {
          code: 'BFCM_15',
          thresholdCents: 7500, // $75 CAD
          percentage: 15,
          label: '15% off $75+'
        },
        {
          code: 'BFCM_10',
          thresholdCents: 5000, // $50 CAD
          percentage: 10,
          label: '10% off $50+'
        }
      ]
      // Add more currencies as needed:
      // EUR: [ { code: 'BFCM_20_EUR', thresholdCents: 7000, percentage: 20, label: '20% off €70+' } ]
    },
    
    // Common configuration
    GWP_PROPERTY_KEY: '_gwp_item',
    GWP_PROPERTY_VALUE: 'true'
  };

  // ============================================================================
  // STATE TRACKING
  // ============================================================================

  let isProcessing = false;
  let lastCartTotal = null;
  let lastCurrency = null;
  let currentDiscountCodes = []; // Track all active discount codes

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Debug logging utility
   */
  function log(message, data = null) {
    if (CONFIG.DEBUG) {
      console.log(`[Unified Promotions] ${message}`, data || '');
    }
  }

  /**
   * Get current cart currency
   */
  function getCurrentCurrency() {
    return window.Shopify?.currency?.active || 
           window.themeVariables?.settings?.cartCurrency || 
           'USD';
  }

  /**
   * Check if promotions are configured for current currency
   */
  function isConfiguredForCurrency(currency) {
    return CONFIG.GWP[currency] !== undefined || CONFIG.TIERS[currency] !== undefined;
  }

  /**
   * Get GWP configuration for currency
   */
  function getGWPConfig(currency) {
    return CONFIG.GWP[currency] || null;
  }

  /**
   * Get tier configuration for currency
   */
  function getTiersConfig(currency) {
    return CONFIG.TIERS[currency] || [];
  }

  /**
   * Check if cart contains a GWP item
   */
  function hasGWPItem(cart) {
    if (!cart || !cart.items) return false;
    
    return cart.items.some(item => {
      const properties = item.properties || {};
      return properties[CONFIG.GWP_PROPERTY_KEY] === CONFIG.GWP_PROPERTY_VALUE;
    });
  }

  /**
   * Calculate cart subtotal excluding GWP items
   */
  function getCartSubtotalExcludingGWP(cart) {
    if (!cart || !cart.items) return 0;
    
    return cart.items.reduce((total, item) => {
      const properties = item.properties || {};
      const isGWPItem = properties[CONFIG.GWP_PROPERTY_KEY] === CONFIG.GWP_PROPERTY_VALUE;
      
      if (isGWPItem) {
        return total; // Don't count GWP items in subtotal
      }
      
      return total + (item.final_line_price || 0);
    }, 0);
  }

  /**
   * Determine which discount tier applies based on cart total and currency
   * Returns the tier object or null if no tier applies
   */
  function getApplicableTier(subtotalCents, currency) {
    const tiers = getTiersConfig(currency);
    
    // Tiers are ordered from highest to lowest, so first match is the best one
    for (const tier of tiers) {
      if (subtotalCents >= tier.thresholdCents) {
        return tier;
      }
    }
    return null;
  }

  /**
   * Determine which discount codes should be active based on cart total and currency
   * Returns array of discount code strings
   */
  function getApplicableDiscountCodes(subtotalCents, currency) {
    const applicableCodes = [];
    
    // Check for tiered discount
    const tier = getApplicableTier(subtotalCents, currency);
    if (tier) {
      applicableCodes.push(tier.code);
    }
    
    // Check for GWP discount
    const gwpConfig = getGWPConfig(currency);
    if (gwpConfig && subtotalCents >= gwpConfig.thresholdCents) {
      applicableCodes.push(gwpConfig.discountCode);
    }
    
    log('Applicable discount codes determined', {
      currency,
      subtotalCents,
      codes: applicableCodes
    });
    
    return applicableCodes;
  }

  // ============================================================================
  // DISCOUNT CODE MANAGEMENT
  // ============================================================================

  /**
   * Apply a single discount code via AJAX
   */
  async function applySingleDiscountCode(discountCode) {
    if (CONFIG.DEBUG) {
      console.log(`[Unified Promotions] 🎫 Attempting to apply discount code: ${discountCode}`);
    }
    
    try {
      const requestBody = { discount: discountCode };
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] Request body:', requestBody);
      }
      
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody)
      });

      if (CONFIG.DEBUG) {
        console.log(`[Unified Promotions] Response status: ${response.status} ${response.statusText}`);
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Unified Promotions] ❌ Discount application failed', { 
          code: discountCode,
          status: response.status, 
          error: errorText 
        });
        throw new Error(`Failed to apply discount: ${response.status}`);
      }

      const result = await response.json();
      if (CONFIG.DEBUG) {
        console.log(`[Unified Promotions] ✅ Discount code ${discountCode} applied successfully`, result);
      }
      return result;

    } catch (error) {
      console.error('[Unified Promotions] ❌ Error applying discount code:', error);
      throw error;
    }
  }

  /**
   * Attempt to apply multiple discount codes
   */
  async function applyMultipleDiscountCodes(discountCodes) {
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🎯 Attempting to apply multiple discount codes:', discountCodes);
    }
    
    if (discountCodes.length === 0) {
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] No discount codes to apply');
      }
      return await removeAllDiscountCodes();
    }
    
    if (discountCodes.length === 1) {
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] Only one discount code, applying normally');
      }
      await applySingleDiscountCode(discountCodes[0]);
      currentDiscountCodes = [discountCodes[0]];
      return;
    }
    
    // Try comma-separated first (most likely to work)
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🧪 Trying comma-separated codes...');
    }
    try {
      const commaSeparated = discountCodes.join(',');
      await applySingleDiscountCode(commaSeparated);
      currentDiscountCodes = discountCodes;
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] ✅ Comma-separated codes worked!');
      }
      return;
    } catch (error) {
      if (CONFIG.DEBUG) {
        console.warn('[Unified Promotions] ⚠️ Comma-separated approach failed:', error.message);
      }
    }
    
    // Try space-separated
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🧪 Trying space-separated codes...');
    }
    try {
      const spaceSeparated = discountCodes.join(' ');
      await applySingleDiscountCode(spaceSeparated);
      currentDiscountCodes = discountCodes;
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] ✅ Space-separated codes worked!');
      }
      return;
    } catch (error) {
      if (CONFIG.DEBUG) {
        console.warn('[Unified Promotions] ⚠️ Space-separated approach failed:', error.message);
      }
    }
    
    // Fallback: Apply only the first (highest priority) discount code
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🔄 FALLBACK: Applying only first code:', discountCodes[0]);
    }
    await applySingleDiscountCode(discountCodes[0]);
    currentDiscountCodes = [discountCodes[0]];
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] ℹ️ Only single discount code applied.');
    }
  }

  /**
   * Remove all discount codes
   */
  async function removeAllDiscountCodes() {
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🗑️ Removing all discount codes');
    }
    
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ 
          discount: '' // Empty string removes discount
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Unified Promotions] ❌ Discount removal failed', { 
          status: response.status, 
          error: errorText 
        });
        throw new Error(`Failed to remove discount: ${response.status}`);
      }

      const result = await response.json();
      currentDiscountCodes = [];
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] ✅ All discount codes removed successfully', result);
      }
      return result;

    } catch (error) {
      console.error('[Unified Promotions] ❌ Error removing discount codes:', error);
      throw error;
    }
  }

  // ============================================================================
  // GWP ITEM MANAGEMENT
  // ============================================================================

  /**
   * Add GWP item to cart
   */
  async function addGWPItem(currency) {
    const gwpConfig = getGWPConfig(currency);
    
    if (!gwpConfig) {
      log('No GWP config for currency', currency);
      return;
    }

    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🎁 Adding GWP item to cart', {
        currency,
        variantId: gwpConfig.variantId
      });
    }

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: gwpConfig.variantId,
            quantity: 1,
            properties: {
              [CONFIG.GWP_PROPERTY_KEY]: CONFIG.GWP_PROPERTY_VALUE
            }
          }]
        })
      });

      if (!response.ok) {
        console.error('[Unified Promotions] ❌ Failed to add GWP item', { status: response.status });
        throw new Error(`Failed to add GWP item: ${response.status}`);
      }

      const result = await response.json();
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] ✅ GWP item added successfully', result);
      }
      return result;

    } catch (error) {
      console.error('[Unified Promotions] ❌ Error adding GWP item:', error);
      throw error;
    }
  }

  /**
   * Remove GWP item from cart
   */
  async function removeGWPItem(cart) {
    if (!cart || !cart.items) return;

    // Find GWP item
    const gwpItem = cart.items.find(item => {
      const properties = item.properties || {};
      return properties[CONFIG.GWP_PROPERTY_KEY] === CONFIG.GWP_PROPERTY_VALUE;
    });

    if (!gwpItem) {
      log('No GWP item found to remove');
      return;
    }

    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🗑️ Removing GWP item from cart', { 
        id: gwpItem.id, 
        key: gwpItem.key 
      });
    }

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: gwpItem.key,
          quantity: 0
        })
      });

      if (!response.ok) {
        console.error('[Unified Promotions] ❌ Failed to remove GWP item', { status: response.status });
        throw new Error(`Failed to remove GWP item: ${response.status}`);
      }

      const result = await response.json();
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] ✅ GWP item removed successfully', result);
      }
      return result;

    } catch (error) {
      console.error('[Unified Promotions] ❌ Error removing GWP item:', error);
      throw error;
    }
  }

  // ============================================================================
  // CART PROCESSING
  // ============================================================================

  /**
   * Process cart changes and manage both GWP and discounts
   */
  async function processCartChange(cart) {
    if (CONFIG.DEBUG) {
      console.log('\n═══════════════════════════════════════════════════════');
      console.log('[Unified Promotions] 🔄 PROCESSING CART CHANGE');
      console.log('═══════════════════════════════════════════════════════');
    }
    
    const currency = getCurrentCurrency();
    
    if (!isConfiguredForCurrency(currency)) {
      log(`Not configured for currency ${currency}, skipping promotion logic`);
      return;
    }

    if (!cart) {
      log('No cart data provided');
      return;
    }

    // Prevent processing if already processing
    if (isProcessing) {
      log('Currently processing, skipping to prevent loops');
      return;
    }

    const subtotal = getCartSubtotalExcludingGWP(cart);
    const hasGWP = hasGWPItem(cart);
    const gwpConfig = getGWPConfig(currency);

    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 📊 Cart Status:', {
        currency,
        subtotal: `${(subtotal / 100).toFixed(2)} ${currency}`,
        subtotalCents: subtotal,
        hasGWP: hasGWP,
        lastCartTotal: lastCartTotal,
        lastCurrency: lastCurrency,
        currentDiscountCodes: currentDiscountCodes,
        itemCount: cart.items.length
      });
    }

    // Check if cart total or currency changed
    const totalChanged = lastCartTotal !== subtotal;
    const currencyChanged = lastCurrency !== currency;
    
    if (!totalChanged && !currencyChanged) {
      log('Cart total and currency unchanged, skipping processing');
      if (CONFIG.DEBUG) {
        console.log('═══════════════════════════════════════════════════════\n');
      }
      return;
    }

    // If currency changed, reset everything
    if (currencyChanged) {
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] 💱 Currency changed:', { from: lastCurrency, to: currency });
      }
      currentDiscountCodes = [];
    }

    lastCartTotal = subtotal;
    lastCurrency = currency;
    isProcessing = true;

    try {
      let needsRefresh = false;

      // Step 1: Manage GWP item (if configured for this currency)
      if (gwpConfig) {
        if (CONFIG.DEBUG) {
          console.log('\n[Unified Promotions] 📦 STEP 1: GWP Item Management');
          console.log('─────────────────────────────────────────────────────');
        }
        
        const shouldHaveGWP = subtotal >= gwpConfig.thresholdCents;
        
        if (CONFIG.DEBUG) {
          console.log('[Unified Promotions] GWP Threshold Check:', {
            threshold: `${(gwpConfig.thresholdCents / 100).toFixed(2)} ${currency}`,
            subtotal: `${(subtotal / 100).toFixed(2)} ${currency}`,
            shouldHaveGWP,
            currentlyHasGWP: hasGWP
          });
        }
        
        if (shouldHaveGWP && !hasGWP) {
          log(`Cart >= threshold - adding GWP item`);
          await addGWPItem(currency);
          needsRefresh = true;
        } else if (!shouldHaveGWP && hasGWP) {
          log(`Cart < threshold - removing GWP item`);
          await removeGWPItem(cart);
          needsRefresh = true;
        } else {
          log('GWP item status is correct, no action needed');
        }
      } else {
        log(`No GWP configured for ${currency}`);
      }

      // Step 2: Manage discount codes
      if (CONFIG.DEBUG) {
        console.log('\n[Unified Promotions] 💳 STEP 2: Discount Code Management');
        console.log('─────────────────────────────────────────────────────');
      }
      
      const applicableDiscountCodes = getApplicableDiscountCodes(subtotal, currency);
      
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] Discount Code Evaluation:', {
          currentCodes: currentDiscountCodes,
          applicableCodes: applicableDiscountCodes,
          needsUpdate: JSON.stringify(currentDiscountCodes) !== JSON.stringify(applicableDiscountCodes)
        });
      }

      // Compare arrays to see if they're different
      const codesChanged = JSON.stringify(currentDiscountCodes.sort()) !== JSON.stringify(applicableDiscountCodes.sort());
      
      if (codesChanged) {
        log('Discount codes need to be updated');
        await applyMultipleDiscountCodes(applicableDiscountCodes);
        needsRefresh = true;
      } else {
        log('Discount codes already correct, no action needed');
      }

      // Step 3: Trigger cart refresh if anything changed
      if (CONFIG.DEBUG) {
        console.log('\n[Unified Promotions] 🔄 STEP 3: Cart Refresh');
        console.log('─────────────────────────────────────────────────────');
      }
      
      if (needsRefresh) {
        log('Fetching updated cart data...');
        const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
        const cartContent = await cartResponse.json();
        
        log('Dispatching cart:refresh event');
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: cartContent,
            source: 'unified-promotions-manager'
          }
        }));
        log('Cart refresh completed');
      } else {
        log('No changes made, skipping cart refresh');
      }

      if (CONFIG.DEBUG) {
        console.log('\n[Unified Promotions] ✅ CART PROCESSING COMPLETE');
      }

    } catch (error) {
      console.error('[Unified Promotions] ❌ ERROR processing cart change:', error);
      console.error('[Unified Promotions] Error stack:', error.stack);
    } finally {
      isProcessing = false;
      if (CONFIG.DEBUG) {
        console.log('═══════════════════════════════════════════════════════\n');
      }
    }
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handle cart:updated events
   */
  function handleCartUpdated(event) {
    log('Cart updated event received', {
      source: event.detail?.source,
      hasCart: !!event.detail?.cart
    });
    
    // Skip if this event came from our own operations
    if (event.detail && event.detail.source === 'unified-promotions-manager') {
      log('Ignoring self-triggered cart:updated');
      return;
    }

    if (event.detail && event.detail.cart) {
      processCartChange(event.detail.cart);
    } else {
      log('Cart updated event has no cart data');
    }
  }

  /**
   * Handle cart:refresh events
   */
  function handleCartRefresh(event) {
    log('Cart refresh event received', {
      source: event.detail?.source,
      hasCart: !!event.detail?.cart
    });
    
    // Skip if this event came from our own operations
    if (event.detail && event.detail.source === 'unified-promotions-manager') {
      log('Ignoring self-triggered cart:refresh');
      return;
    }

    if (event.detail && event.detail.cart) {
      processCartChange(event.detail.cart);
    } else {
      log('Cart refresh event has no cart data');
    }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * Initialize Unified Promotions Manager
   */
  function init() {
    if (CONFIG.DEBUG) {
      console.log('\n╔═══════════════════════════════════════════════════════╗');
      console.log('║   UNIFIED PROMOTIONS MANAGER - INITIALIZATION        ║');
      console.log('╚═══════════════════════════════════════════════════════╝');
      
      console.log('[Unified Promotions] 🔧 Configuration:', {
        debugMode: CONFIG.DEBUG,
        gwpCurrencies: Object.keys(CONFIG.GWP),
        tiersCurrencies: Object.keys(CONFIG.TIERS),
        gwpDetails: CONFIG.GWP,
        tiersDetails: CONFIG.TIERS
      });
    }
    
    const currency = getCurrentCurrency();
    log(`Current currency: ${currency}`);
    
    if (!isConfiguredForCurrency(currency)) {
      log(`Not configured for ${currency}, not initializing promotion logic`);
      return;
    }

    log(`${currency} is configured - initializing...`);

    // Listen to cart events
    log('Attaching event listeners...');
    document.documentElement.addEventListener('cart:updated', handleCartUpdated);
    document.documentElement.addEventListener('cart:refresh', handleCartRefresh);
    log('Event listeners attached (cart:updated, cart:refresh)');

    // Initial check on page load
    if (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.cartUrl) {
      log('Fetching initial cart state...');
      fetch(`${window.themeVariables.routes.cartUrl}.js`)
        .then(response => response.json())
        .then(cart => {
          log('Initial cart loaded', {
            itemCount: cart.items?.length || 0,
            totalPrice: cart.total_price
          });
          processCartChange(cart);
        })
        .catch(error => {
          console.error('[Unified Promotions] ❌ Error fetching initial cart:', error);
        });
    } else {
      console.warn('[Unified Promotions] ⚠️ Cart URL not available in themeVariables');
    }

    log('Initialization complete\n');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ============================================================================
  // GLOBAL API
  // ============================================================================

  // Expose configuration and status globally
  window.UnifiedPromotionsManager = {
    config: CONFIG,
    getCurrentCurrency: getCurrentCurrency,
    getCurrentDiscountCodes: () => currentDiscountCodes,
    getStatus: () => ({
      isProcessing,
      lastCartTotal,
      lastCurrency,
      currentDiscountCodes,
      currency: getCurrentCurrency()
    }),
    isConfiguredForCurrency: isConfiguredForCurrency,
    getGWPConfig: getGWPConfig,
    getTiersConfig: getTiersConfig
  };

  // Expose additional functions for debugging (only in debug mode)
  if (CONFIG.DEBUG) {
    log('Debug mode enabled - exposing additional functions');
    Object.assign(window.UnifiedPromotionsManager, {
      processCartChange,
      hasGWPItem,
      getCartSubtotalExcludingGWP,
      getApplicableTier,
      getApplicableDiscountCodes,
      addGWPItem,
      removeGWPItem,
      applySingleDiscountCode,
      applyMultipleDiscountCodes,
      removeAllDiscountCodes
    });
  }

})();
